// Number and date formatting utilities

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const percentFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const percentValueFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const numberFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

const dateTimeFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export function formatCurrency(value: number): string {
  return currencyFormatter.format(value === 0 ? 0 : value);
}

export function formatPercent(value: number): string {
  // If value is already a percentage (e.g., 5 for 5%), format directly
  // If value is a decimal (e.g., 0.05 for 5%), convert first
  const isDecimal = Math.abs(value) < 1 && value !== 0;
  if (isDecimal) {
    return percentFormatter.format(value);
  }
  return `${percentValueFormatter.format(value)}%`;
}

export function formatNumber(value: number): string {
  return numberFormatter.format(value);
}

export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return dateFormatter.format(d);
}

export function formatDateTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return dateTimeFormatter.format(d);
}

export function formatPnL(value: number): string {
  const formatted = formatCurrency(Math.abs(value));
  return value >= 0 ? `+${formatted}` : `-${formatted}`;
}

export function formatPnLPercent(value: number): string {
  const formatted = formatPercent(Math.abs(value));
  return value >= 0 ? `+${formatted}` : `-${formatted}`;
}

/**
 * Calculate remaining calendar days from the browser's local date to the avoidance date.
 * Partial days round down to whole calendar days.
 *
 * @param avoidanceDate - ISO 8601 UTC datetime string (e.g., "2025-07-02T00:00:00+00:00")
 * @returns Number of calendar days remaining (0 or greater)
 */
export function calculateDaysRemaining(avoidanceDate: string): number {
  const target = new Date(avoidanceDate);
  const now = new Date();

  // Compare using local calendar dates (strip time component)
  const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const targetLocal = new Date(target.getFullYear(), target.getMonth(), target.getDate());

  const diffMs = targetLocal.getTime() - todayLocal.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  return Math.max(diffDays, 0);
}

/**
 * Format remaining days until avoidance date as a human-readable string.
 * Returns "Today" (0 days), "1 day left" (1 day), or "{N} days left" (≥2 days).
 *
 * @param avoidanceDate - ISO 8601 UTC datetime string (e.g., "2025-07-02T00:00:00+00:00")
 * @returns Formatted string indicating days remaining
 */
export function formatDaysRemaining(avoidanceDate: string): string {
  const days = calculateDaysRemaining(avoidanceDate);

  if (days === 0) return 'Today';
  if (days === 1) return '1 day left';
  return `${days} days left`;
}
